import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

const KEY = "dv_consent";

// Public, analytics-eligible routes. Everything else (dashboard, vault, identity,
// verification, payment, protected viewer, shares) is treated as sensitive.
function isPublic(path) {
  return (
    ["/", "/login", "/register", "/for-institutions"].includes(path) ||
    path.startsWith("/legal") ||
    path.startsWith("/countries")
  );
}

// Single source of truth for what analytics may run right now.
function enforce(path) {
  const ph = window.posthog;
  if (!ph) return;
  const accepted = localStorage.getItem(KEY) === "accepted";
  const allow = accepted && isPublic(path);
  try {
    if (ph.config) ph.config.autocapture = allow; // autocapture only on consented public pages
    if (allow) {
      ph.opt_in_capturing?.();
      ph.startSessionRecording?.();
    } else {
      // Decline, no-choice, or any sensitive route → no capture, no recording, no autocapture.
      ph.stopSessionRecording?.();
      ph.opt_out_capturing?.();
    }
  } catch { /* posthog not ready / unavailable */ }
}

export default function ConsentBanner() {
  const loc = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => { if (!localStorage.getItem(KEY) && !loc.pathname.startsWith("/sign")) setShow(true); }, [loc.pathname]);

  // Re-enforce on every navigation (and poll briefly until posthog has loaded).
  useEffect(() => {
    let tries = 0;
    enforce(loc.pathname);
    const id = setInterval(() => {
      enforce(loc.pathname);
      if (window.posthog || tries > 20) clearInterval(id);
      tries += 1;
    }, 200);
    return () => clearInterval(id);
  }, [loc.pathname]);

  const choose = (v) => {
    localStorage.setItem(KEY, v);
    enforce(loc.pathname);
    setShow(false);
  };

  if (!show || loc.pathname.startsWith("/sign")) return null;
  return (
    <div className="fixed bottom-4 inset-x-4 max-w-[calc(100vw-2rem)] sm:inset-x-auto sm:right-6 sm:max-w-sm z-[60]" data-testid="consent-banner">
      <div className="relative rounded-2xl border border-white/10 bg-[#141418] shadow-2xl p-4 pr-8">
        <button onClick={() => choose("declined")} aria-label="Dismiss and decline" data-testid="consent-dismiss-btn"
          className="absolute top-2.5 right-2.5 text-white/40 hover:text-white/80 text-lg leading-none">×</button>
        <p className="text-xs text-white/80 leading-relaxed">
          We use cookies &amp; privacy-safe analytics to improve DuruVault. Analytics stay off inside your vault, and recordings never run on document, identity or payment pages.
        </p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={() => choose("accepted")} className="rounded-full flex-1 h-8" data-testid="consent-accept-btn">Accept</Button>
          <Button size="sm" onClick={() => choose("declined")} variant="outline" className="rounded-full flex-1 h-8 bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="consent-decline-btn">Decline</Button>
        </div>
      </div>
    </div>
  );
}
